import type { User } from '~/types/user'

export interface PolicyDocument {
  id: number
  version: string
  name: string
  text: string
  updated_at: string
}

export const usePrivacyPolicy = () => {
  const fetchPolicy = async (): Promise<PolicyDocument | null> => {
    try {
      const { $api } = useNuxtApp()
      return await $api<PolicyDocument>('/system/documents', {
        params: { type: 'user_agreement' }
      })
    } catch {
      return null
    }
  }

  const needsAcceptance = (user: User, policy: PolicyDocument): boolean => {
    return user.accepted_document_id !== policy.id
  }

  const acceptPolicy = async (documentId: number): Promise<void> => {
    const { $api } = useNuxtApp()
    const { setUser } = useUser()

    const response = await $api<{ status: string; data?: User }>('/user/profile-information', {
      method: 'POST',
      body: { accepted_document_id: documentId }
    })

    if (response.status === 'success' && response.data) {
      setUser(response.data)
    }
  }

  const checkAndPromptPolicy = async (onAccepted: () => void, onDeclined: () => void): Promise<void> => {
    const { user } = useUser()
    const store = usePrivacyPolicyModalStore()

    const policy = await fetchPolicy()
    if (policy && user.value && needsAcceptance(user.value, policy)) {
      store.open({
        closeable: false,
        doc: policy,
        onAgree: async (documentId) => {
          await acceptPolicy(documentId)
          onAccepted()
        },
        onDecline: onDeclined
      })
    } else {
      onAccepted()
    }
  }

  return { fetchPolicy, needsAcceptance, acceptPolicy, checkAndPromptPolicy }
}
